import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cn } from "../lib/cn.js";
/**
 * A money field that cannot be typed wrong.
 *
 * The old pattern across the app was a free-text `<Input inputMode="decimal">`
 * parsed on submit by a lenient parser. It accepted `1234,56`, `1.234,56`,
 * `1234` and `R$ 12,50` — and that leniency is the problem, not the feature:
 * `1.234` is a thousand-something to a Brazilian and one-point-two to the
 * parser, and whichever way that guess is resolved it is wrong for somebody.
 * A field where `29000` might mean twenty-nine thousand or twenty-nine
 * thousand centavos is a field that will eventually book the wrong figure.
 *
 * So the separators are not typed at all. Digits enter from the right and the
 * centavos fill first — `2` is R$ 0,02, `2900000` is R$ 29.000,00 — which is
 * how every till, card terminal and banking app in the country behaves, and
 * therefore what the hands of anybody who will use this already know.
 *
 * ## Why the arithmetic is string arithmetic
 *
 * This package may depend on React, Radix and Tailwind only, so `@aeris/money`
 * is out of reach. That constraint turns out to be the right design anyway:
 * the mask never converts anything to a `number`, so there is no point at
 * which a centavo could be lost to a float. It moves a string of digits
 * around and inserts punctuation for display, and the value it emits is the
 * same fixed-point decimal string the wire and `@aeris/money` already speak.
 */
/** Everything that is not 0-9, discarded. Punctuation is ours to place. */
const digitsOf = (raw) => raw.replace(/\D/g, '');
/** `007` → `7`. Nothing else; a lone zero is handled by `normalise`. */
const withoutLeadingZeros = (digits) => digits.replace(/^0+/, '');
/**
 * The one canonical form, so display and wire always agree.
 *
 * Leading zeros go — `007` is `7` — and a run of nothing but zeros collapses to
 * a single `0`, because **zero is a number**. Only an empty string means "no
 * number here"; deciding WHEN an all-zero edit becomes empty belongs to
 * `nextDigits`, which is the one place that can see the difference.
 */
const normalise = (digits) => {
    const trimmed = withoutLeadingZeros(digits);
    if (trimmed !== '')
        return trimmed;
    return digits === '' ? '' : '0';
};
/** `"2900000"`, 2 → `"29.000,00"`. Display only. */
const formatDigits = (raw, scale) => {
    const digits = normalise(raw);
    if (digits === '')
        return '';
    const padded = digits.padStart(scale + 1, '0');
    // The padStart above guarantees at least one whole digit, so `0,05` keeps
    // the zero it needs to be read as five centavos.
    const whole = padded.slice(0, padded.length - scale);
    const cents = scale === 0 ? '' : padded.slice(-scale);
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return scale === 0 ? grouped : `${grouped},${cents}`;
};
/** `"2900000"`, 2 → `"29000.00"`. What the API is given. */
const digitsToWire = (raw, scale) => {
    const digits = normalise(raw);
    if (digits === '')
        return '';
    const padded = digits.padStart(scale + 1, '0');
    const whole = padded.slice(0, padded.length - scale);
    return scale === 0 ? whole : `${whole}.${padded.slice(-scale)}`;
};
/**
 * `"29000.00"`, 2 → `"2900000"`. What a form loads when editing a saved row.
 *
 * Truncates rather than rounds a value carrying more precision than the field
 * shows, because this runs when a form OPENS: silently rewriting a stored
 * figure the moment somebody looks at it would let an unrelated edit save a
 * number nobody chose.
 */
const wireToDigits = (wire, scale) => {
    const trimmed = wire.trim();
    if (trimmed === '')
        return '';
    const [whole = '', fraction = ''] = trimmed.replace(/^[+-]/, '').split('.');
    return normalise(`${digitsOf(whole)}${digitsOf(fraction).padEnd(scale, '0').slice(0, scale)}`);
};
/**
 * What the field holds after an edit — and the one place zero is decided.
 *
 * **Typing zero must be possible.** A till that opened and took no money is
 * closed by counting R$ 0,00, and until F028a that was unenterable in every
 * money field in the product: the answer was "Informe o valor contado", printed
 * over a field displaying `0,00`, which reads as the system refusing an answer
 * it had just been given.
 *
 * Deciding it from the digits alone cannot work, because the MASK is made of
 * zeros. Backspacing `0,01` deletes the `1` and leaves the text `0,0` — whose
 * digits are `00`, character for character what somebody typing two zeros
 * produces. The difference is not in the result; it is in the direction.
 *
 * So the two are told apart by what the field SHOWED against what it now
 * holds: shorter means a deletion, longer means a keystroke. Compared as
 * rendered text, separators and all, because that is what the browser edited —
 * comparing digit counts would call backspacing `0,01` an insertion, since the
 * mask's own zeros outnumber the single digit the value normalises to.
 *
 * **Removing can empty the field; adding cannot.** Backspacing to nothing
 * arrives at an empty field, which a required check reads as "não digitei
 * nada"; typing `0` into an empty field arrives at zero, which is an answer.
 */
const nextDigits = (shown, typed) => {
    const digits = digitsOf(typed);
    const meaningful = withoutLeadingZeros(digits);
    if (meaningful !== '')
        return meaningful;
    // Nothing typed at all: an empty field, whichever way it got there.
    if (digits === '')
        return '';
    return typed.length > shown.length ? '0' : '';
};
export const MoneyInput = forwardRef(({ className, value, onValueChange, invalid = false, scale = 2, symbol = true, placeholder, ...props }, ref) => {
    const digits = wireToDigits(value, scale);
    /*
     * What the field displays — and the yardstick an edit is measured against.
     *
     * A zero the person typed and a zero left behind by the mask are the same
     * digits; only the direction of the edit tells them apart, and direction is
     * a fact about this text. See `nextDigits`.
     */
    const shown = formatDigits(digits, scale);
    return (_jsxs("div", { className: cn('flex h-(--control-height-md) w-full items-center gap-1.5 rounded-(--radius-md)', 'border border-(--border-default) bg-(--surface) px-2.5', 'transition-colors duration-(--duration-fast)', 'hover:border-(--border-strong)', 
        // The ring lands on the wrapper because that is what looks focused;
        // the inner input drops its own so the two do not stack. Same shape
        // as the global focus style in base.css.
        'focus-within:outline focus-within:outline-2 focus-within:outline-(--border-focus)', invalid ? 'border-(--danger)' : '', props.disabled === true
            ? 'cursor-not-allowed bg-(--surface-sunken) opacity-60'
            : '', className), children: [symbol ? (_jsx("span", { "aria-hidden": "true", className: "shrink-0 select-none text-(length:--text-sm) text-(--text-muted)", children: "R$" })) : null, _jsx("input", { ...props, ref: ref, type: "text", 
                // `decimal` rather than `numeric` so a phone shows a keypad; the
                // separator key it offers is inert here, which is the point.
                inputMode: "decimal", autoComplete: "off", "aria-invalid": invalid || undefined, value: shown, placeholder: placeholder ?? (scale === 0 ? '0' : `0,${'0'.repeat(scale)}`), onChange: (event) => {
                    onValueChange(digitsToWire(nextDigits(shown, event.target.value), scale));
                }, onFocus: (event) => {
                    // The caret belongs at the end: every digit typed is a digit
                    // appended, so a caret parked mid-number would insert somewhere
                    // the mask is about to move anyway.
                    const input = event.currentTarget;
                    requestAnimationFrame(() => {
                        input.setSelectionRange(input.value.length, input.value.length);
                    });
                    props.onFocus?.(event);
                }, onClick: (event) => {
                    const input = event.currentTarget;
                    input.setSelectionRange(input.value.length, input.value.length);
                    props.onClick?.(event);
                }, className: cn('h-full w-full min-w-0 border-0 bg-transparent p-0 outline-none', 'text-right font-(--font-mono) text-(length:--text-sm) text-(--text-primary)', 'tabular-nums placeholder:text-(--text-muted)', 'disabled:cursor-not-allowed') })] }));
});
MoneyInput.displayName = 'MoneyInput';
//# sourceMappingURL=money-input.js.map